// sensorInfo — operator-facing descriptions of each enforcement area,
// grounded in the BlueRock Unplugged MVP VI Testplan (Confluence EN space,
// page 2592833537, release bru-release-26-23-0). Voice: what it watches,
// what attack it stops, honest caveats. BlueRock vocabulary: a triggered
// rule is a "violation"; observe mode logs it (WARN), remediation blocks
// it (ERROR).
//
// The MCP areas (tools / launch / content filters) are NOT covered by the
// testplan — their copy derives from bru_policy.json semantics (policy-edit
// skill); revisit when the MCP sensor doc lands.

export type SensorArea =
  | "mcp-tools"
  | "mcp-launch"
  | "content-filters"
  | "processes"
  | "files"
  | "network"
  | "kernel"
  | "agent-tools"
  | "models"
  | "spend";

export const SENSOR_INFO: Record<SensorArea, { blurb: string }> = {
  "mcp-tools": {
    blurb:
      "Filters individual MCP tool calls per server at the message boundary — a denied tool call raises a violation and, in block mode, never reaches the server.",
  },
  "mcp-launch": {
    blurb:
      "Controls which MCP servers may start or connect at all — stdio launches at spawn time, SSE and streamable-HTTP at the connection handshake. Already-running servers are unaffected.",
  },
  "content-filters": {
    blurb:
      "Brace inspects the content crossing the sandbox boundary and blocks on match: regex filters over request arguments, descriptions, and responses (the BRU schema adds prompt filters, message-size thresholds, and a weighted prompt-injection classifier) — stopping secrets leaving or injected instructions arriving.",
  },
  "agent-tools": {
    blurb:
      "Per-agent tool guard for coding assistants (Claude Code, Gemini CLI, Codex CLI, Cursor): denies named tools at the agent's own lifecycle hooks — e.g. forbid Bash or WebFetch entirely — in observe or block mode, independent of MCP rules. The special value MCP denies every MCP-provided tool.",
  },
  processes: {
    blurb:
      "Controls which executables may run in the sandbox: a glob allowlist of trusted paths, a deny list for binaries that must never run (the classic download-and-exfiltrate trio nc / wget / curl), and per-program exceptions. Violations fire at execve.",
  },
  files: {
    blurb:
      "Watches access to paths you mark sensitive — SSH keys, cloud and AI-agent credentials, /etc auth files, /proc snooping. Each path watches every access or writes only; per-program exceptions exempt system binaries like sshd and sudo.",
  },
  network: {
    blurb:
      "Per-sandbox egress allowlist enforced at the kernel firewall: only listed destinations (with port and protocol restriction) are reachable; every other packet is dropped and logged. With no firewall configured, egress is open and ingress is closed.",
  },
  kernel: {
    blurb:
      "Seccomp-based syscall interception: every exec, file open, socket, connect, and delete is recorded with full process context — the audit trail of what an agent actually did. mmap_exec (fileless execution) and suspicious_syscalls (unshare/ptrace escapes) can block; the rest are observational by design — process policy belongs to Processes, egress to Network.",
  },
  models: {
    blurb:
      "Restricts which LLM models the sandbox may call. Violations can be blocked or transparently downgraded to an approved model so the agent's task continues. Allowlist mode is default-deny for models that appear after this policy was written.",
  },
  spend: {
    blurb:
      "Token and cost guardrails on LLM calls: per-call token caps and a refill-rate token bucket stop runaway agent loops. In block mode a breaching request is clamped to fit the budget or refused outright. Cost telemetry currently covers SDK/framework calls, not the coding-agent sensors.",
  },
};
